import { ApiCall } from '../ApiClient';
import { BoardLiteData, ResultData } from '../ApiTypes';
import { dateTransformer } from '../ApiUtil';

const get = <TResponse>(def: Omit<ApiCall<never, TResponse>, 'method'>): ApiCall<never, TResponse> =>
    Object.assign(def, { method: 'get' });

const post = <TResponse>(def: Omit<ApiCall<any, TResponse>, 'method'>): ApiCall<any, TResponse> =>
    Object.assign(def, { method: 'post' });

const put = <TResponse>(def: Omit<ApiCall<any, TResponse>, 'method'>): ApiCall<any, TResponse> =>
    Object.assign(def, { method: 'put' });

export type FeedbackBody = { type: string, message: string };

export interface TemplateData {
    id: string;
    label: string;
    description?: string;
    source: string;
    special?: boolean;
    thumbUrl: string;
    time: string;
    people: string;
    complexity: number;
}

export interface TemplateGroupData {
    id: string;
    label: string;
    icon?: string;
    special?: boolean;
    items: TemplateData[];
}

export type TemplateFindOneQuery = { templateId: string } | { source: string };

export interface TemplateCreateBody {
    label?: string;
    folder: string;
    board: string;
}

export interface TemplateUpdateBody {
    templateId: string;
    board: string;
}

export interface NewTemplateTagData {
    id: string;
    name: string;
    label: string;
    color: string;
    sort: number;
}

export interface NewTemplateData {
    id: string;
    accountId?: string;
    alias: string;
    label: string;
    description: string;
    time?: number;
    people?: number;
    complexity?: number;
    thumbUrl: string;
    thumbEtag: string;
    version: number;
    publishedAt: Date;
    createdAt: Date;
    tags: NewTemplateTagData[];
    metadata: Record<string, any>;
}

export interface NewTemplateCreateBody {
    source: string;
    name: string;
    desc: string;
    tags: string[];
}

export interface TemplatePatchBody {
    templateId: string;
    label?: string;
}

export type TemplateThumbInfo = {
    ready: boolean,
    url: string,
};

export const templateEndpoints = {

    findOne: (query: TemplateFindOneQuery) => get<TemplateData>({
        path: '/templates.findOne',
        query,
    }),

    snapshot: (boardId: string) => get<any>({
        path: '/templates.snapshot',
        query: { boardId },
    }),

    snapshot2: (templateId: string) => get<any>({
        path: '/templates.snapshot2',
        query: { templateId },
    }),

    list: () => get<TemplateGroupData[]>({
        path: '/templates.list',
        transform: dateTransformer(),
    }),

    list2: (filter?: 'all' | 'team') => get<NewTemplateData[]>({
        path: '/templates.list2',
        query: { filter },
        transform: dateTransformer(),
    }),

    create: (body: TemplateCreateBody) => post<BoardLiteData>({
        path: '/templates.create',
        body,
    }),

    create2: (body: NewTemplateCreateBody) => post<{ executionId: string }>({
        path: '/templates.create2',
        body,
    }),

    update: (body: TemplateUpdateBody) => post<ResultData>({
        path: '/templates.update',
        body,
    }),

    patch: (body: TemplatePatchBody) => post<ResultData>({
        path: '/templates.update',
        body,
    }),

    delete: (templateId: string) => post<ResultData>({
        path: '/templates.delete',
        body: { templateId },
    }),

    thumb: (templateId: string) => get<TemplateThumbInfo>({
        path: '/templates.thumb',
        query: { templateId },
    }),

    stars: {

        list: () => get<string[]>({
            path: '/templates.stars.list',
        }),

        create: (templateId: string) => post<ResultData>({
            path: '/templates.stars.create',
            body: { templateId },
        }),

        delete: (templateId: string) => post<ResultData>({
            path: '/templates.stars.delete',
            body: { templateId },
        }),
    },

    tags: {

        list: (search?: string) => get<NewTemplateTagData[]>({
            path: '/templates.tags.list',
            query: { search },
        }),

    }
};
